import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useInvalidateTenantQueries } from '@/hooks/useTenantQuery';
import axios from 'axios';
import { esfService, icsPositionService, assignmentService, myPositionService, positionSelfAssignService, positionSettingsService } from '@/services/position.service';
import type {
  CreateESFRequest,
  UpdateESFRequest,
  CreateICSPositionRequest,
  UpdateICSPositionRequest,
  CreateAssignmentRequest,
  UpdateAssignmentRequest,
  ICSSection,
  PositionSettings,
  QualifiedPosition,
  PositionRequest,
} from '@/types/position';

/** Don't retry on 403 (Forbidden) -- the user simply lacks the permission. */
function skipRetryOn403(_failureCount: number, error: Error): boolean {
  if (axios.isAxiosError(error) && error.response?.status === 403) return false;
  return true;
}

// ESF hooks
export function useESFs() {
  return useQuery({
    queryKey: ['esfs'],
    queryFn: esfService.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: skipRetryOn403,
  });
}

export function useESF(id: string) {
  return useQuery({
    queryKey: ['esfs', id],
    queryFn: () => esfService.getById(id),
    enabled: !!id,
  });
}

export function useCreateESF() {
  const queryClient = useQueryClient();
  const invalidateTenant = useInvalidateTenantQueries();

  return useMutation({
    mutationFn: (data: CreateESFRequest) => esfService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['esfs'] });
      invalidateTenant.invalidate(['esfs']);
    },
  });
}

export function useUpdateESF() {
  const queryClient = useQueryClient();
  const invalidateTenant = useInvalidateTenantQueries();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateESFRequest }) =>
      esfService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['esfs'] });
      invalidateTenant.invalidate(['esfs']);
      queryClient.invalidateQueries({ queryKey: ['esfs', id] });
      invalidateTenant.invalidate(['esfs', id]);
    },
  });
}

// ICS Position hooks
export function useICSPositions(section?: ICSSection) {
  return useQuery({
    queryKey: ['ics-positions', section],
    queryFn: () => icsPositionService.getAll(section),
    staleTime: 5 * 60 * 1000,
    retry: skipRetryOn403,
  });
}

export function useICSPosition(id: string) {
  return useQuery({
    queryKey: ['ics-positions', id],
    queryFn: () => icsPositionService.getById(id),
    enabled: !!id,
  });
}

export function useCreateICSPosition() {
  const queryClient = useQueryClient();
  const invalidateTenant = useInvalidateTenantQueries();

  return useMutation({
    mutationFn: (data: CreateICSPositionRequest) => icsPositionService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ics-positions'] });
      invalidateTenant.invalidate(['ics-positions']);
    },
  });
}

export function useUpdateICSPosition() {
  const queryClient = useQueryClient();
  const invalidateTenant = useInvalidateTenantQueries();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateICSPositionRequest }) =>
      icsPositionService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['ics-positions'] });
      invalidateTenant.invalidate(['ics-positions']);
      queryClient.invalidateQueries({ queryKey: ['ics-positions', id] });
      invalidateTenant.invalidate(['ics-positions', id]);
    },
  });
}

// Position Assignment hooks
export function useIncidentAssignments(incidentId: string | undefined, currentOnly: boolean = true) {
  return useQuery({
    queryKey: ['assignments', incidentId, currentOnly],
    queryFn: () => assignmentService.getByIncident(incidentId!, currentOnly),
    enabled: !!incidentId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useAssignment(id: string) {
  return useQuery({
    queryKey: ['assignments', id],
    queryFn: () => assignmentService.getById(id),
    enabled: !!id,
  });
}

export function useCreateAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAssignmentRequest) => assignmentService.create(data),
    onSuccess: (assignment) => {
      queryClient.invalidateQueries({ queryKey: ['assignments', assignment.incident_id] });
    },
  });
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAssignmentRequest }) =>
      assignmentService.update(id, data),
    onSuccess: (assignment) => {
      queryClient.invalidateQueries({ queryKey: ['assignments', assignment.incident_id] });
      queryClient.invalidateQueries({ queryKey: ['assignments', assignment.id] });
    },
  });
}

export function useRelieveAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => assignmentService.relieve(id),
    onSuccess: (assignment) => {
      queryClient.invalidateQueries({ queryKey: ['assignments', assignment.incident_id] });
    },
  });
}

export function useDeleteAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => assignmentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
    },
  });
}

// Admin hooks for ICS Positions & ESFs management
export function useICSPositionsAdmin() {
  return useQuery({
    queryKey: ['ics-positions', 'admin'],
    queryFn: icsPositionService.getAdmin,
    staleTime: 30 * 1000,
  });
}

export function useESFsAdmin() {
  return useQuery({
    queryKey: ['esfs', 'admin'],
    queryFn: esfService.getAdmin,
    staleTime: 30 * 1000,
  });
}

export function useResetICSPosition() {
  const queryClient = useQueryClient();
  const invalidateTenant = useInvalidateTenantQueries();
  return useMutation({
    mutationFn: (id: string) => icsPositionService.reset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ics-positions'] });
      invalidateTenant.invalidate(['ics-positions']);
    },
  });
}

export function useResetESF() {
  const queryClient = useQueryClient();
  const invalidateTenant = useInvalidateTenantQueries();
  return useMutation({
    mutationFn: (id: string) => esfService.reset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['esfs'] });
      invalidateTenant.invalidate(['esfs']);
    },
  });
}

export function useDeactivateICSPosition() {
  const queryClient = useQueryClient();
  const invalidateTenant = useInvalidateTenantQueries();
  return useMutation({
    mutationFn: (id: string) => icsPositionService.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ics-positions'] });
      invalidateTenant.invalidate(['ics-positions']);
    },
  });
}

export function useDeactivateESF() {
  const queryClient = useQueryClient();
  const invalidateTenant = useInvalidateTenantQueries();
  return useMutation({
    mutationFn: (id: string) => esfService.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['esfs'] });
      invalidateTenant.invalidate(['esfs']);
    },
  });
}

export function useReorderICSPositions() {
  const queryClient = useQueryClient();
  const invalidateTenant = useInvalidateTenantQueries();
  return useMutation({
    mutationFn: (positions: { id: string; sort_order: number }[]) =>
      icsPositionService.reorder(positions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ics-positions'] });
      invalidateTenant.invalidate(['ics-positions']);
    },
  });
}

export function useMyCurrentPosition(incidentId?: string) {
  return useQuery({
    queryKey: ['my-position', incidentId],
    queryFn: () => myPositionService.get(incidentId!),
    enabled: !!incidentId,
    staleTime: 60 * 1000,
  });
}

/**
 * Returns the calling user's current ICS position code per incident across
 * every incident they hold an active assignment in. Used by the Supply
 * Request Approval Queue (cross-incident) to disable Approve buttons when
 * the user lacks an eligible position.
 */
export function useMyCurrentPositions() {
  return useQuery<Array<{ incident_id: string; position_code: string | null }>>({
    queryKey: ['my-current-positions'],
    queryFn: () => myPositionService.getAllCurrent(),
    staleTime: 60 * 1000,
    retry: skipRetryOn403,
  });
}

// Self-assignment query hooks
export function useQualifiedPositions(incidentId?: string) {
  return useQuery<QualifiedPosition[]>({
    queryKey: ['qualified-positions', incidentId],
    queryFn: () => positionSelfAssignService.getQualified(incidentId!),
    enabled: !!incidentId,
    staleTime: 60 * 1000,
  });
}

export function usePositionRequests(incidentId?: string) {
  return useQuery<PositionRequest[]>({
    queryKey: ['position-requests', incidentId],
    queryFn: () => positionSelfAssignService.getRequests(incidentId!),
    enabled: !!incidentId,
    staleTime: 30 * 1000,
  });
}

export function useMyPositionRequests(incidentId?: string) {
  return useQuery<PositionRequest[]>({
    queryKey: ['my-position-requests', incidentId],
    queryFn: () => positionSelfAssignService.getMyRequests(incidentId!),
    enabled: !!incidentId,
    staleTime: 30 * 1000,
  });
}

export function usePositionSettings() {
  return useQuery<PositionSettings>({
    queryKey: ['positionSettings'],
    queryFn: () => positionSettingsService.get(),
    staleTime: 5 * 60 * 1000,
    retry: skipRetryOn403,
  });
}

// Self-assignment mutation hooks
export function useSelfAssignPosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: { ics_position_id?: string; esf_id?: string; custom_title?: string; notes?: string } }) =>
      positionSelfAssignService.selfAssign(incidentId, data),
    onSuccess: (_, { incidentId }) => {
      queryClient.invalidateQueries({ queryKey: ['assignments', incidentId] });
      queryClient.invalidateQueries({ queryKey: ['qualified-positions', incidentId] });
      queryClient.invalidateQueries({ queryKey: ['my-position', incidentId] });
    },
  });
}

export function useCreatePositionRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: { ics_position_id?: string; esf_id?: string; custom_title?: string; notes?: string } }) =>
      positionSelfAssignService.createRequest(incidentId, data),
    onSuccess: (_, { incidentId }) => {
      queryClient.invalidateQueries({ queryKey: ['my-position-requests', incidentId] });
    },
  });
}

export function useReviewPositionRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, data }: { requestId: string; data: { action: 'approve' | 'deny'; notes?: string } }) =>
      positionSelfAssignService.reviewRequest(requestId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['position-requests'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
    },
  });
}

export function useCancelPositionRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      positionSelfAssignService.cancelRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-position-requests'] });
    },
  });
}

export function useUpdatePositionSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<PositionSettings>) =>
      positionSettingsService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positionSettings'] });
    },
  });
}

export function useSwitchPosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: { ics_position_id?: string; esf_id?: string; relieve_current: boolean } }) =>
      positionSelfAssignService.switch(incidentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-position'] });
      queryClient.invalidateQueries({ queryKey: ['incident-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['qualified-positions'] });
    },
  });
}
